import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const PUBLIC_BASE = `${API_ORIGIN}/api/portal/info-center`
const ADMIN_BASE = `${API_ORIGIN}/api/admin/portal/info-center`

export type InfoCategoryKey =
  | 'NOTICE'
  | 'NEWSLETTER'
  | 'CARE'
  | 'CONSULT'
  | 'FAQ'
  | 'ONE'
  | 'ARCHIVE'
  | 'FORM'
  | 'GUIDE'
  | 'DB'
  | 'LAW'
  | 'STAT'
export type InfoLayout = 'panel' | 'split-left' | 'split-right' | 'overlay' | 'banner' | 'text'
export type InfoAlign = 'left' | 'center' | 'right'
export type InfoVAlign = 'top' | 'center' | 'bottom'

export interface InfoStyle {
  titleWeight: number
  vAlign: InfoVAlign
  bgColor: string
  overlayColor: string
  overlayOpacity: number
  imageX: number
  imageY: number
  bannerHeight: number
  padX: number
  padY: number
  blockW: number
  thumbX: number
  thumbY: number
  thumbZoom: number
}

export interface InfoStage {
  kicker: string
  title: string
  desc: string
  heroImage: string
  hasPrev?: boolean
}

export interface InfoStory {
  idx: number
  sourceKind: 'ANNOUNCEMENT' | 'NEWSLETTER' | 'INFO'
  sourceCode: string
  category: InfoCategoryKey
  categoryLabel: string
  title: string
  summary: string
  thumb: string
  heroImage: string
  place: string
  note: string
  whenText: string
  featured: boolean
  featureSort: number
  layout: InfoLayout
  titleSize: number
  titleColor: string
  titleAlign: InfoAlign
  textX: number
  textY: number
  style: InfoStyle
  status: 'PUBLISHED' | 'HIDDEN'
  publishedAt: string
  href: string
  hasPrev?: boolean
}

export interface InfoCenterBundle {
  stage: InfoStage
  counts: Record<string, number>
  categories: Array<{ key: InfoCategoryKey; label: string }>
  featured: InfoStory[]
  stories: InfoStory[]
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다.')
  }
  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) throw new Error('요청 처리에 실패했습니다.')
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || result.data == null) throw new Error(result.message || fallback)
  return result.data
}

export async function fetchInfoCenter(query: { category?: string; keyword?: string; sort?: string } = {}) {
  const params = new URLSearchParams()
  if (query.category) params.set('category', query.category)
  if (query.keyword) params.set('keyword', query.keyword)
  if (query.sort) params.set('sort', query.sort)
  const qs = params.toString()
  const result = await parseJson<InfoCenterBundle>(
    await apiFetch(`${PUBLIC_BASE}${qs ? `?${qs}` : ''}`, { credentials: 'include' }),
  )
  return unwrap(result, '정보마당을 불러오지 못했습니다.')
}

export async function fetchInfoStory(idx: number) {
  const result = await parseJson<{ story: InfoStory }>(
    await apiFetch(`${PUBLIC_BASE}/stories/${idx}`, { credentials: 'include' }),
  )
  return unwrap(result, '소식을 불러오지 못했습니다.').story
}

export async function fetchAdminInfoCenter(query: { category?: string; keyword?: string; sort?: string } = {}) {
  const params = new URLSearchParams()
  if (query.category) params.set('category', query.category)
  if (query.keyword) params.set('keyword', query.keyword)
  if (query.sort) params.set('sort', query.sort)
  const qs = params.toString()
  const result = await parseJson<InfoCenterBundle>(
    await apiFetch(`${ADMIN_BASE}${qs ? `?${qs}` : ''}`, { credentials: 'include' }),
  )
  return unwrap(result, '정보마당을 불러오지 못했습니다.')
}

export async function uploadInfoImage(file: File) {
  const body = new FormData()
  body.append('image', file)
  const result = await parseJson<{ url: string }>(
    await apiFetch(`${ADMIN_BASE}/images`, { method: 'POST', credentials: 'include', body }),
  )
  return unwrap(result, '이미지를 올리지 못했습니다.').url
}

export async function saveInfoStory(idx: number, payload: Partial<InfoStory>) {
  const result = await parseJson<{ story: InfoStory }>(
    await apiFetch(`${ADMIN_BASE}/${idx}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
  return unwrap(result, '소식을 저장하지 못했습니다.').story
}

export async function restoreInfoStory(idx: number) {
  const result = await parseJson<{ story: InfoStory }>(
    await apiFetch(`${ADMIN_BASE}/${idx}/restore`, { method: 'POST', credentials: 'include' }),
  )
  return unwrap(result, '이전 설정으로 되돌리지 못했습니다.').story
}

export async function createInfoStory(payload: Partial<InfoStory>) {
  const result = await parseJson<{ story: InfoStory }>(
    await apiFetch(ADMIN_BASE, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
  return unwrap(result, '소식을 등록하지 못했습니다.').story
}

export async function saveInfoStage(payload: Partial<InfoStage>) {
  const result = await parseJson<{ stage: InfoStage }>(
    await apiFetch(`${ADMIN_BASE}/stage`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
  return unwrap(result, '상단을 저장하지 못했습니다.').stage
}

export async function restoreInfoStage() {
  const result = await parseJson<{ stage: InfoStage }>(
    await apiFetch(`${ADMIN_BASE}/stage/restore`, { method: 'POST', credentials: 'include' }),
  )
  return unwrap(result, '상단을 되돌리지 못했습니다.').stage
}
